import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { NAV_ITEMS } from "./navConfig";

/**
 * Breadcrumb · per memory ota_breadcrumb_standard.md
 *  - List 2 级 / Detail 3 级 / Wizard 3 级
 *  - 不加「资源/管理」中间层
 *  - 不用英文
 */

interface Crumb {
  label: string;
  to?: string;
}

const FIRST_CRUMB: Crumb = { label: "首页", to: "/" };

// 静态映射：sub path → 中文标签（自动补 detail/wizard 等动作词）
const SUB_LABELS: Record<string, string> = {
  new: "新建",
  edit: "编辑",
  detail: "详情",
  wizard: "向导",
  inventory: "库存",
  pricing: "定价",
  schedule: "排程",
  hotels: "酒店管理",
  vehicles: "车辆管理",
  drivers: "司导管理",
  attractions: "门票管理",
  suppliers: "供应商管理",
  products: "产品管理",
  orders: "订单中心",
  "site-payments": "官网收款",
  "site-service": "客服与收款",
  handoff: "OP 交接中心",
  dispatch: "调度中心",
  "channel-partners": "同业 B2B",
  // 售价管理中心子页（唯一 segment·避免与 products/settings 全局标签冲突）
  channels: "渠道与系数",
  "price-book": "同行价目表",
  "sell-config": "售价配置",
  product: "产品售价",
  "vehicle-operations": "车务台账",
  finance: "财务",
  "api-integration": "渠道中心",
  analytics: "运营总览",
  permissions: "权限管理",
  settings: "系统设置",
  places: "地点清单",
  admin: "",
  ops: "",
  // T-S2-B-4
  alerts: "告警",
  system: "系统",
  policies: "价格 Policy",

  // ── UIFIX-1·D（2026-09-10）补齐 ────────────────────────────────────────────
  // 本文件顶部注释写着「不用英文」，但 segLabel() 查不到就 `decodeURIComponent(seg)`
  // 原样吐英文。拿 routes.tsx 的全部非参数 segment 对了一遍这张表，**缺 59 个**——
  // 也就是说系统里一大片二级页的顶栏面包屑长成「首页 › 产品管理 › 详情 › schedule-calendar」，
  // 而同一屏页面自己那条面包屑写的是中文「排期管理」。
  // 🔴 下面每个标签都取自**该页面自己的标题**（PageShell title / H1 / 页内面包屑末级），
  //    没有另造词；emoji 和「· English」尾巴一律去掉，面包屑只要短名。
  //    重跑核对的脚本见 tickets/ticket-UIFIX1-manual-sweep-fixes.md 附录。
  // 订单
  cancel: "取消订单",
  checkout: "快速完整下单",
  confirmation: "出团确认单",
  financials: "价格明细",
  timeline: "时间线",
  voucher: "出团凭证",
  "order-calendar": "订单日历",
  "ctrip-wanle-roster": "携程玩乐名单",   // WANLE-2
  // 产品 / 资源
  "schedule-calendar": "排期日历",
  options: "选项",   // OPT-C1
  "inventory-calendar": "库存日历",
  "tape-chart": "运营总表",
  rooms: "房型",
  "ticket-types": "票种",
  "price-policies": "价格 Policy",
  policy: "价格 Policy",
  "seat-calendar": "座位日历",
  "seat-layout": "座位布局",
  "inventory-guard": "库存护栏",
  licenses: "证照管理",
  "qr-tokens": "QR Token",
  // 调度
  assign: "派单向导",
  "driver-schedule": "司导排班",
  // CRM / 运营
  customers: "CRM 中心",
  complaints: "客户投诉处理",
  deep: "深度分析",
  "channel-usage": "渠道客户用量",
  // 售价管理中心
  "change-requests": "价格变更日志",
  "channel-markups": "渠道加价",
  "customer-overrides": "客户专属定价",
  overrides: "B2B 专属价",
  holidays: "节假日加价",
  tiers: "客户分级",
  // 同业 B2B
  approvals: "额度审批",
  settlements: "对账结算",
  workbench: "客户经理工作台",
  "open-api": "开放平台审批",
  // 财务
  payables: "应付账款",
  receivables: "应收账款",
  invoices: "发票管理",
  reports: "财务报表",
  expenses: "费用报销",   // ⚠️ 同一个 segment 两处在用（财务=司导报销 / 车务=车务费用），
                          //    平表只能给一个标签，取中性词。要分开得改成按完整路径匹配。
  // 车务台账
  maintenance: "维修工单",
  license: "证照费用",
  upload: "上传",
  // 权限
  matrix: "权限矩阵",
  roles: "角色管理",
  "audit-logs": "审计日志",
  trash: "回收站",
  // 渠道中心 / 系统设置
  "sync-logs": "全局同步日志",
  "error-queue": "错误队列",
  qb: "QuickBooks",
  notifications: "通知配置",
  "cancellation-policy": "取消条款",
  "confirmation-template": "出团确认单模板",
  "demo-tools": "演示数据工具",
  import: "数据批量导入",
  "dashboard-legacy": "旧版看板",
  // 注：login / sso / review / mfa-enroll 是后台外壳**之外**的独立页，不渲染本面包屑，
  //     故意不补——补了也不会显示。
};

// 中间段不可点：这些"集合词"无独立路由（如 /hotels/policies 缺 hotelId 会加载失败）·渲染纯文本
const NON_LINK_SEGMENTS = new Set(["policies", "policy", "rooms", "ticket-types", "price-policies", "licenses", "tours"]);

// 同名段在不同模块下语义不同时，用完整路径覆盖 SUB_LABELS——只放真有歧义的，
// 不要把 SUB_LABELS 整张搬过来（工单 UIFIX-2）。键是从根开始累加的完整路径前缀
// （buildCrumbs 里的 acc），优先于 SUB_LABELS/segLabel 命中。
// 🔴 linkable: false 用于 /dispatch/orders——它没有独立路由（只有
// /dispatch/orders/:id），不标 false 会被当成可点链接，点下去白屏。
// 未显式给 linkable 的条目（如下面两条 expenses）沿用原有的按位置/NON_LINK_SEGMENTS
// 判定——它们本身是合法路由（如 /admin/vehicle-operations/expenses/upload 的父级）。
const PATH_LABELS: Record<string, { label: string; linkable?: boolean }> = {
  "/dispatch/orders": { label: "派车单", linkable: false },
  "/admin/vehicle-operations/expenses": { label: "车务费用" },
  "/admin/finance/expenses": { label: "司导报销" },
};

export function Breadcrumb() {
  const { pathname } = useLocation();
  const crumbs = buildCrumbs(pathname);
  return (
    <nav data-testid="breadcrumb" className="flex items-center gap-2">
      {crumbs.map((c, idx) => {
        const isLast = idx === crumbs.length - 1;
        return (
          <div key={idx} className="flex items-center gap-2">
            {idx > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />}
            {c.to && !isLast ? (
              <Link to={c.to} className="text-sm text-slate-500 hover:text-slate-900">
                {c.label}
              </Link>
            ) : (
              <span className={isLast ? "text-sm font-semibold text-slate-900" : "text-sm text-slate-500"}>
                {c.label}
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function segLabel(seg: string): string {
  if (SUB_LABELS[seg]) return SUB_LABELS[seg];
  if (UUID_RE.test(seg)) return "详情";   // :id 段不显示原始 UUID
  // 🔴 UIFIX-1·D：以前这里 `return decodeURIComponent(seg)`，查不到就把英文路径段
  //    直接印在面包屑上（"schedule-calendar"），违反本文件顶部「不用英文」那条。
  //    改成返回空串——buildCrumbs 里的 `if (label)` 会跳过空标签，面包屑短一级，
  //    总好过中英文混排。表补全了，正常路由不会走到这里。
  return "";
}

function buildCrumbs(pathname: string): Crumb[] {
  if (pathname === "/" || pathname === "") return [FIRST_CRUMB];
  const parts = pathname.split("/").filter(Boolean);
  const crumbs: Crumb[] = [FIRST_CRUMB];
  // PAY-1 · 官网收款挂在订单中心下（样稿：首页 / 订单中心 / 官网收款）
  if (pathname === "/admin/site-payments") return [FIRST_CRUMB, { label: "订单中心", to: "/orders" }, { label: "官网收款" }];

  // CTRIP-P1 · 携程对照表挂在「上架中心 › 平台上架」下（样稿 ④：首页 › 上架中心 › 平台上架 › 携程对照表）
  if (pathname === "/admin/listing/ctrip-lookups") return [FIRST_CRUMB, { label: "上架中心", to: "/admin/listing" }, { label: "平台上架", to: "/admin/listing?tab=platform" }, { label: "携程对照表" }];

  // 一级模块 = NAV_ITEMS 内匹配最长前缀
  const moduleItem = NAV_ITEMS.find(
    (n) => n.to !== "/" && (pathname === n.to || pathname.startsWith(n.to + "/")),
  );
  if (moduleItem) {
    crumbs.push({ label: moduleItem.labelCn, to: moduleItem.to });
    // 拼接剩余 segment
    const rest = pathname.replace(moduleItem.to, "").split("/").filter(Boolean);
    let acc = moduleItem.to;
    rest.forEach((seg, i) => {
      acc += "/" + seg;
      const override = PATH_LABELS[acc];
      const label = override ? override.label : segLabel(seg);
      if (label) {
        const defaultLinkable = i !== rest.length - 1 && !NON_LINK_SEGMENTS.has(seg);
        const linkable = override?.linkable === false ? false : defaultLinkable;
        crumbs.push({ label, to: linkable ? acc : undefined });
      }
    });
    return crumbs;
  }

  // fallback：按 segment 翻译
  let acc = "";
  parts.forEach((seg, i) => {
    acc += "/" + seg;
    const override = PATH_LABELS[acc];
    const label = override ? override.label : segLabel(seg);
    if (label) {
      const defaultLinkable = i !== parts.length - 1 && !NON_LINK_SEGMENTS.has(seg);
      const linkable = override?.linkable === false ? false : defaultLinkable;
      crumbs.push({ label, to: linkable ? acc : undefined });
    }
  });
  return crumbs;
}
